import React, { useState } from 'react';
import { motion } from 'framer-motion';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

const OpdrachtZoeken = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');

  const categories = [
    { value: 'all', label: 'Alle Vakgebieden', icon: 'Grid' },
    { value: 'solar', label: 'Zonnepanelen', icon: 'Sun' },
    { value: 'heatpump', label: 'Warmtepompen', icon: 'Thermometer' },
    { value: 'battery', label: 'Thuisbatterijen', icon: 'Battery' },
    { value: 'maintenance', label: 'Technisch onderhoud', icon: 'Wrench' },
    { value: 'ventilation', label: 'Ventilatie & klimaat', icon: 'Wind' }
  ];

  const mockJobs = [
    {
      id: 1,
      title: 'Vakmensen gezocht voor zonnepanelenproject woonwijk',
      company: 'DuurzaamBouw BV',
      category: 'solar',
      location: 'Amsterdam',
      budget: '€15.000 - €18.000',
      deadline: '2026-02-15',
      urgency: 'high',
      description: 'Wij zoeken ervaren vakmensen voor installatie van 24 zonnepanelen op schuin dak inclusief omvormer en monitoring systeem'
    },
    {
      id: 2,
      title: 'Warmtepomp monteurs nieuwbouwproject',
      company: 'EcoTech Solutions',
      category: 'heatpump',
      location: 'Rotterdam',
      budget: '€22.500',
      deadline: '2026-01-25',
      urgency: 'urgent',
      description: 'Personeel gezocht voor installatie lucht-water warmtepomp 12kW met aansluiting op vloerverwarming'
    },
    {
      id: 3,
      title: 'Batterijsysteem specialisten met zonnepanelen ervaring',
      company: 'GreenEnergy NL',
      category: 'battery',
      location: 'Utrecht',
      budget: '€12.000',
      deadline: '2026-02-01',
      urgency: 'normal',
      description: 'Vakmensen nodig voor installatie thuisbatterij 10kWh met integratie bestaande zonnepanelen'
    },
    {
      id: 4,
      title: 'Onderhoudsmonteurs voor technische installaties',
      company: 'Facility Masters',
      category: 'maintenance',
      location: 'Den Haag',
      budget: '€3.500',
      deadline: '2026-03-10',
      urgency: 'normal',
      description: 'Personeel gezocht voor jaarlijks onderhoud warmtepomp, ventilatie en zonnepanelen bedrijfspand'
    }
  ];

  const urgencyColors = {
    low: 'bg-green-100 text-green-700 border-green-200',
    normal: 'bg-blue-100 text-blue-700 border-blue-200',
    high: 'bg-orange-100 text-orange-700 border-orange-200',
    urgent: 'bg-red-100 text-red-700 border-red-200'
  };

  const urgencyLabels = {
    low: 'Laag',
    normal: 'Normaal',
    high: 'Hoog',
    urgent: 'Urgent'
  };

  const filteredJobs = mockJobs?.filter(job => {
    const matchesSearch = job?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         job?.company?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || job?.category === selectedCategory;
    const matchesLocation = selectedLocation === 'all' || job?.location === selectedLocation;
    return matchesSearch && matchesCategory && matchesLocation;
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  return (
    <MainLayout>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <motion.div variants={itemVariants} className="mb-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
              <Icon name="Search" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground">
                Project Zoeken
              </h1>
              <p className="text-muted-foreground mt-1">
                Ontdek beschikbare verduurzamingsprojecten van bedrijven in verschillende vakgebieden
              </p>
            </div>
          </div>

          <div className="bg-card rounded-xl border border-border p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Zoeken
                </label>
                <Input
                  type="text"
                  placeholder="Zoek op titel of bedrijf..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  icon="Search"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Categorie
                </label>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e?.target?.value)}
                >
                  {categories?.map(cat => (
                    <option key={cat?.value} value={cat?.value}>{cat?.label}</option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Locatie
                </label>
                <Select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e?.target?.value)}
                >
                  <option value="all">Alle Locaties</option>
                  <option value="Amsterdam">Amsterdam</option>
                  <option value="Rotterdam">Rotterdam</option>
                  <option value="Utrecht">Utrecht</option>
                  <option value="Den Haag">Den Haag</option>
                </Select>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="mb-6">
          <div className="flex items-center justify-between">
            <p className="text-muted-foreground">
              <span className="font-semibold text-foreground">{filteredJobs?.length}</span> projecten gevonden
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Icon name="SlidersHorizontal" size={16} className="mr-2" />
                Filters
              </Button>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 gap-4">
          {filteredJobs?.map((job, index) => (
            <motion.div
              key={job?.id}
              variants={itemVariants}
              custom={index}
              whileHover={{ scale: 1.02 }}
              className="bg-card rounded-xl border border-border p-6 hover:border-primary transition-all cursor-pointer"
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon 
                        name={categories?.find(c => c?.value === job?.category)?.icon || 'Briefcase'} 
                        size={24} 
                        color="var(--color-primary)" 
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-foreground mb-1">
                        {job?.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {job?.company}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${urgencyColors?.[job?.urgency]}`}>
                      {urgencyLabels?.[job?.urgency]}
                    </span>
                  </div>

                  <p className="text-muted-foreground mb-4">
                    {job?.description}
                  </p>

                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Icon name="MapPin" size={16} />
                      <span>{job?.location}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Icon name="Calendar" size={16} />
                      <span>Deadline: {new Date(job.deadline)?.toLocaleDateString('nl-NL')}</span>
                    </div>
                    <div className="flex items-center gap-2 text-primary font-semibold">
                      <Icon name="Euro" size={16} />
                      <span>{job?.budget}</span>
                    </div>
                  </div>
                </div>

                <div className="flex lg:flex-col gap-2">
                  <Button className="flex-1 lg:flex-none">
                    <Icon name="Send" size={16} className="mr-2" />
                    Reageren
                  </Button>
                  <Button variant="outline" className="flex-1 lg:flex-none">
                    <Icon name="Bookmark" size={16} className="mr-2" />
                    Opslaan
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {filteredJobs?.length === 0 && (
          <motion.div
            variants={itemVariants}
            className="text-center py-12 bg-card rounded-xl border border-border"
          >
            <Icon name="Search" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Geen opdrachten gevonden
            </h3>
            <p className="text-muted-foreground">
              Probeer andere zoekfilters of kom later terug voor nieuwe opdrachten
            </p>
          </motion.div>
        )}
      </motion.div>
    </MainLayout>
  );
};

export default OpdrachtZoeken;